import {FC, useState, FormEvent} from 'react';
import {PaymentElement, useStripe, useElements} from '@stripe/react-stripe-js';
import {Box, Button, CircularProgress, Typography} from '@mui/material';
import {useIntl} from 'react-intl';

interface GuestPaymentFormProps {
    onSuccess: () => void;
    onError: (message: string) => void;
}

export const GuestPaymentForm: FC<GuestPaymentFormProps> = ({onSuccess, onError}) => {
    const intl = useIntl();
    const stripe = useStripe();
    const elements = useElements();
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [isFormComplete, setIsFormComplete] = useState(false);

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();

        if (!stripe || !elements) {
            return;
        }

        setIsLoading(true);
        setErrorMessage(null);

        try {
            const {error} = await stripe.confirmPayment({
                elements,
                confirmParams: {
                    return_url: window.location.href,
                },
                redirect: 'if_required',
            });

            if (error) {
                // Show error to the customer
                if (error.type === 'card_error' || error.type === 'validation_error') {
                    setErrorMessage(error.message || 'Payment failed');
                    onError(error.message || 'Payment failed');
                } else {
                    setErrorMessage('An unexpected error occurred');
                    onError('An unexpected error occurred');
                }
            } else {
                // Payment succeeded or is processing
                // The WebSocket will notify us when the payment is confirmed
                onSuccess();
            }
        } catch (err: any) {
            setErrorMessage(err.message || 'Payment failed');
            onError(err.message || 'Payment failed');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} style={{display: 'flex', flexDirection: 'column', flex: 1}}>
            <Box sx={{marginBottom: 2, paddingBottom: '80px'}}>
                <PaymentElement
                    options={{
                        layout: 'tabs',
                    }}
                    onChange={(event) => {
                        setIsFormComplete(event.complete);
                    }}
                />
            </Box>

            {errorMessage && (
                <Typography
                    sx={{
                        color: '#d32f2f',
                        fontSize: 13,
                        marginBottom: 1,
                        textAlign: 'center',
                    }}
                >
                    {errorMessage}
                </Typography>
            )}

            <Box
                sx={{
                    position: 'fixed',
                    bottom: 0,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: '100%',
                    maxWidth: '480px',
                    backgroundColor: '#fff',
                    boxSizing: 'border-box',

                    '@media (min-width: 1024px)': {
                        bottom: '24px',
                        overflow: 'hidden',
                        borderRadius: '0 0 16px 16px',
                    },
                }}
            >
                <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    fullWidth
                    disabled={!stripe || !elements || isLoading || !isFormComplete}
                    sx={{
                        height: 56,
                        fontSize: 16,
                        fontWeight: 600,
                        borderRadius: 0,
                        textTransform: 'none',

                        '@media (min-width: 600px)': {
                            height: 60,
                            fontSize: 17,
                        },
                    }}
                >
                    {isLoading ? (
                        <CircularProgress size={24} color="inherit" />
                    ) : (
                        intl.formatMessage({id: 'guestCharging.confirmPayment'})
                    )}
                </Button>
            </Box>
        </form>
    );
};

export default GuestPaymentForm;
